import { useEffect, useState } from 'react';
import Modal from '../Modal.tsx';
import { api } from '../../lib/api.ts';
import type { PersonNameUsage } from '../../../shared/api.ts';

/**
 * Offered straight after a person is renamed: the decks that still print their
 * old name, and the chance to bring them up to date.
 *
 * Decks snapshot a bank person, so nothing propagates on its own — which is
 * deliberate, and why this is an offer rather than something that has already
 * happened by the time the dialog appears. Skipping is a first-class answer;
 * an old deck saying what it said on the day is often the right outcome.
 */
export default function PersonRenameDecksModal({
  itemId,
  from,
  to,
  onClose,
  onDone,
}: {
  itemId: string;
  from: string;
  to: string;
  onClose: () => void;
  onDone: (updated: number, skipped: number) => void;
}) {
  const [usage, setUsage] = useState<PersonNameUsage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .get<PersonNameUsage>(`/api/bank/items/${encodeURIComponent(itemId)}/usage?from=${encodeURIComponent(from)}`)
      .then((u) => {
        if (!live) return;
        setUsage(u);
        // Every deck pre-ticked: someone who opened this dialog is here to
        // update them, and unticking one is easier than finding the one.
        setPicked(u.decks.map((d) => d.slug));
      })
      .catch((e: unknown) => live && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      live = false;
    };
  }, [itemId, from]);

  async function submit() {
    if (!picked.length) return;
    setBusy(true);
    try {
      const result = await api.post<{ updated: unknown[]; skipped: unknown[] }>(
        `/api/bank/items/${encodeURIComponent(itemId)}/rename-in-decks`,
        { from, targets: picked.map((slug) => ({ slug })) },
      );
      onDone(result.updated.length, result.skipped.length);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  }

  const slideCount = (slug: string) => usage?.decks.find((d) => d.slug === slug)?.slides.length ?? 0;

  return (
    <Modal
      title="Update decks that still say &ldquo;{from}&rdquo;?"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn--ghost" onClick={onClose} disabled={busy}>
            {usage?.decks.length ? 'Leave them' : 'Close'}
          </button>
          {usage && usage.decks.length > 0 && (
            <button type="button" className="btn btn--primary" onClick={submit} disabled={busy || !picked.length}>
              {busy ? 'Updating…' : `Update ${picked.length} deck${picked.length === 1 ? '' : 's'}`}
            </button>
          )}
        </>
      }
    >
      {error && <p className="person-rename__error">{error}</p>}
      {!usage && !error && <p className="muted person-rename__status">Checking every deck…</p>}
      {usage && usage.decks.length === 0 && (
        <p className="muted person-rename__status">
          No deck prints that name{usage.complete ? '.' : ', but not every deck could be checked.'}
        </p>
      )}
      {usage && usage.decks.length > 0 && (
        <>
          <ul className="cat-picker" role="list">
            {usage.decks.map((d) => (
              <li key={d.slug}>
                <label className="cat-picker__row">
                  <input
                    type="checkbox"
                    checked={picked.includes(d.slug)}
                    onChange={() =>
                      setPicked((prev) =>
                        prev.includes(d.slug) ? prev.filter((s) => s !== d.slug) : [...prev, d.slug],
                      )
                    }
                  />
                  <span>
                    {d.slug} <span className="muted person-rename__count">({slideCount(d.slug)} slides)</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          <p className="muted person-rename__note" style={{ marginTop: '0.5rem' }}>
            {picked.length
              ? `The name on those slides becomes "${to}". Portraits and everything else stay as they are.`
              : 'Nothing selected — every deck keeps the old name.'}
          </p>
        </>
      )}
      {usage && !usage.complete && (
        <p className="muted person-rename__note" style={{ marginTop: '0.5rem' }}>
          Some decks could not be read, so this list may be short. Anything missed keeps the old name and can be fixed
          on the slide.
        </p>
      )}
    </Modal>
  );
}
